import type { ComponentType, JSX, SVGProps } from 'react';
import { motion } from 'framer-motion';
import { revealUp, revealItem, staggerParent, inViewOnce, useRevealGate } from './anim';
import { SectionGlow } from './SectionGlow';
import {
  IconVoice,
  IconScreen,
  IconFace,
  IconCollab,
  IconRooms,
  IconCharacter,
  IconTheme,
  IconSparkle,
  IconWatch,
  IconPlay,
  IconCosmetics,
  IconMap,
} from './icons';

/**
 * The capability grid — each is a real, SHIPPED thing the world does, said in one warm line, paired with
 * an icon from our OWN cohesive amber icon set (no OS emoji — see {@link icons}). Note "Room voice" uses
 * a POSITIVE sound-waves glyph: the old crossed-out 🔇 wrongly signalled *muted*.
 *
 * Twelve cards (3 rows of 4 at `lg`) deliberately span the WHOLE product — voice/video/share, the social
 * fabric (rooms, play, celebration), identity/cosmetics, and authoring — so the page stops under-selling
 * features that already exist.
 */
const FEATURES: { Icon: ComponentType<SVGProps<SVGSVGElement>>; title: string; line: string }[] = [
  { Icon: IconVoice, title: 'Room voice', line: 'Walk into a room and just talk — everyone there hears you, clear and equal.' },
  { Icon: IconFace, title: 'Show your face', line: 'Pop your camera on as a little bubble beside your character, only when you want.' },
  { Icon: IconScreen, title: 'Screen share', line: 'Share your screen to the room beside you — no links, no "can you see this?"' },
  { Icon: IconWatch, title: 'Watch together', line: 'Queue up YouTube and take turns as DJ — the whole room watches in sync.' },
  { Icon: IconCollab, title: 'Real collaboration', line: 'Gather round a desk, pair up, or start a huddle wherever you stand.' },
  { Icon: IconRooms, title: 'Rooms & doors', line: 'Lounges, focus zones, meeting rooms — wander freely, close a door for a private huddle.' },
  { Icon: IconPlay, title: 'Play together', line: 'Emotes, reactions, a 70-sound board and quick mini-games — your team’s own in-jokes.' },
  { Icon: IconSparkle, title: 'Celebrate wins', line: 'Confetti when you ship, jukebox discos, a world that throws a party for real work.' },
  { Icon: IconCharacter, title: 'Custom characters', line: 'Twenty critters, your colours, hats and props. Be exactly you.' },
  { Icon: IconCosmetics, title: 'Earned cosmetics & pets', line: 'Unlock hats, outfits and a little pet that follows you — earned, never pay-to-win.' },
  { Icon: IconTheme, title: 'Your space, your way', line: 'Theme your world, rearrange the furniture, make the place truly yours.' },
  { Icon: IconMap, title: 'Build your own', line: 'Design a whole custom space in the map editor — an office, a café, a cinema — and drop your people straight in.' },
];

/**
 * Features — a responsive grid of glass cards. The grid staggers its children in on scroll; each card has
 * a subtle hover lift + glow. All motion gated behind reduced-motion.
 */
export function FeaturesSection(): JSX.Element {
  const reduce = useRevealGate();
  return (
    <section id="features" aria-labelledby="features-heading" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-28 lg:px-8">
      <SectionGlow tone="cool" side="right" />
      <motion.div
        variants={reduce ? undefined : revealUp}
        initial={reduce ? false : 'hidden'}
        whileInView={reduce ? undefined : 'show'}
        viewport={inViewOnce}
        className="mx-auto max-w-2xl text-center"
      >
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-300/80">Everything you need</p>
        <h2 id="features-heading" className="mt-4 text-balance text-3xl font-bold tracking-tight text-white sm:text-5xl">
          Built for being together
        </h2>
        <p className="mt-4 text-lg text-white/60">
          Not another meeting tool. A living space with everything that makes being side-by-side feel good.
        </p>
      </motion.div>

      <motion.ul
        variants={reduce ? undefined : staggerParent}
        initial={reduce ? false : 'hidden'}
        whileInView={reduce ? undefined : 'show'}
        viewport={inViewOnce}
        className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4"
      >
        {FEATURES.map((f) => (
          <motion.li
            key={f.title}
            variants={reduce ? undefined : revealItem}
            className="group rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.015] p-6 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:border-amber-300/30 hover:from-white/[0.09] hover:shadow-[0_20px_50px_-20px_rgba(255,210,63,0.3)]"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-300/10 text-amber-300 ring-1 ring-amber-300/20 transition group-hover:scale-110">
              <f.Icon className="h-6 w-6" />
            </div>
            <h3 className="mt-4 text-lg font-bold text-white">{f.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-white/60">{f.line}</p>
          </motion.li>
        ))}
      </motion.ul>
    </section>
  );
}
